<template>
  <div class="ai-report-container">
    <!-- 顶部标题栏 -->
    <div class="header">
      <h2>AI辅助诊断系统（注：AI生成内容仅供参考，具体病理请详问医生）</h2>
    </div>
    
    <!-- 聊天主界面 -->
    <div class="main-area">
      <!-- 消息列表区域 -->
      <div class="chat-messages" ref="chatMessages">
        <!-- AI欢迎消息 -->
        <div class="message ai-message">
          <div class="avatar">AI</div>
          <div class="message-content">
            <p>您好，我是医院AI助手。我可以帮您分析医学影像报告并提供诊断建议。请上传您的检查报告或直接提问。</p>
          </div>
        </div>
        
        <!-- 用户消息与附件 -->
        <div class="message user-message">
          <div class="message-content">
            <p>请帮我分析一下这份胸部CT报告，并进行肿瘤分期。</p>
            
            <!-- 附件区域 -->
            <div class="attachment-card">
              <div class="attachment-header" @click="toggleAttachment(0)">
                <i class="attachment-icon">📄</i>
                <span class="attachment-name">胸部CT报告.pdf</span>
                <span class="attachment-action">{{ showAttachment ? '收起' : '查看' }}</span>
              </div>
              
              <!-- 附件内容展示区 -->
              <div v-if="showAttachment" class="attachment-content">
                <div class="report-content">
                  <h4>胸部CT报告</h4>
                  <p><strong>检查日期：</strong>2025年5月10日</p>
                  <p><strong>结合多平面重建：</strong></p>
                  <p><strong>胸廓：</strong>双侧胸廓对称，双侧肋骨密度未见确切异常，胸壁软组织未见确切异常密度影。</p>
                  <p><strong>肺结节：</strong>右肺上叶尖段亚实性结节（Srs304/Img35），大小约11mm×8mm（长径×短径），稍牵拉邻近胸膜，病灶实性部分较少，强化程度无法评估，周围未见确切增粗血管影。双肺另见少许小结节影，约2-4mm。</p>
                  <p><strong>肺部：</strong>双肺未见肺气肿征象；双肺少量条索影；气管、左右主支气管、各叶和段支气管通畅，管壁未见增厚、狭窄征象，管腔内未见确切异常密度影。双侧肺门不大，肺门未见确切异常密度影。</p>
                  <p><strong>纵隔、心血管：</strong>纵隔居中，结构清晰，其内未见确切异常密度影及肿大淋巴结。心脏形态、大小正常，冠状动脉移行区少许钙化灶，心脏瓣膜区未见确切异常钙化灶，心包未见确切异常。双侧胸膜无增厚，双侧胸膜腔未见确切积液征象。</p>
                  <p><strong>肝脏：</strong>肝内散在小类圆形低密度，边界清楚，增强未见强化，大者位于肝左外叶，约0.9cm×0.6cm；肝脏形态大小未见异常，边缘光滑，肝裂未见增宽；肝内外胆管未见扩张；</p>
                  <p><strong>胆囊：</strong>充盈良好，壁及腔内未见异常密度影，周围脂肪间隙清晰；</p>
                  <p><strong>胰腺：</strong>形态、大小未见异常，实质密度均匀、未见异常密度及强化影，周围脂肪间隙清晰；主胰管未见扩张及狭窄；</p>
                  <p><strong>脾脏：</strong>未见增大，实质密度均匀、未见异常密度及强化，周围脂肪间隙清晰；</p>
                  <p><strong>胃：</strong>充盈良好，壁未见增厚。</p>
                  <p><strong>其他：</strong>腹腔未见积液，腹腔内、腹膜后及腹主动脉旁未见肿大淋巴结显示。</p>
                </div>
                
                <div class="report-image">
                  <img src="https://via.placeholder.com/350x250?text=肺部CT影像" alt="肺部CT影像">
                  <p class="image-caption">右肺上叶尖段结节影像</p>
                </div>
              </div>
            </div>
          </div>
          <div class="avatar user">用户</div>
        </div>
        
        <!-- AI回复 -->
        <div class="message ai-message">
          <div class="avatar">AI</div>
          <div class="message-content">
            <div class="ai-analysis">
              <h3>诊断分析结果：</h3>
              
              <div class="diagnosis-section">
                <h4>诊断：</h4>
                <p>原发性右肺腺癌。</p>
              </div>
              
              <div class="diagnosis-section">
                <h4>TNM分期：</h4>
                <ul>
                  <li><strong>T指标：</strong>T1a（肿瘤最大径≤3cm，且不涉及支气管或肺顶部结构）。</li>
                  <li><strong>N指标：</strong>N0（无区域淋巴结转移）。</li>
                  <li><strong>M指标：</strong>M0（无远处转移）。</li>
                </ul>
              </div>
              
              <div class="diagnosis-section">
                <h4>分期结果：</h4>
                <p>T1aN0M0，即IA期。</p>
              </div>
              
              <div class="diagnosis-section">
                <h4>分析依据：</h4>
                <p>1. 右肺上叶尖段发现11mm×8mm的亚实性结节，符合早期肺癌影像学特征</p>
                <p>2. 无纵隔或肺门淋巴结肿大证据，提示无淋巴结转移</p>
                <p>3. 未见远处器官转移证据</p>
                <p>4. 根据最新AJCC肺癌分期标准，T1a（≤1cm）N0M0属于IA期</p>
              </div>
              
              <div class="diagnosis-section">
                <h4>治疗建议：</h4>
                <p>1. 建议进一步完善病理活检以明确诊断</p>
                <p>2. 早期肺癌可考虑手术切除，如肺叶切除术或肺段切除术</p>
                <p>3. 术后定期随访，包括胸部CT和肿瘤标志物检测</p>
                <p>4. 建议戒烟并避免接触肺部致癌物质</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 输入区域 -->
      <div class="input-area">
        <div class="attachment-bar">
          <div class="selected-files">
            <div v-if="hasAttachment" class="selected-file">
              <span>胸部CT报告.pdf</span>
              <button class="remove-btn">×</button>
            </div>
          </div>
          <button class="attach-button">
            <i class="attach-icon">📎</i>
            添加附件
          </button>
        </div>
        
        <div class="message-input">
          <textarea 
            placeholder="请输入您的问题..." 
            v-model="inputMessage"
          ></textarea>
          <button class="send-button" :disabled="!inputMessage.trim()">发送</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIReportAG',
  data() {
    return {
      inputMessage: '',
      hasAttachment: true,
      showAttachment: true
    }
  },
  methods: {
    toggleAttachment(index) {
      this.showAttachment = !this.showAttachment;
    }
  }
}
</script>

<style scoped>
.ai-report-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

/* 头部区域 */
.header {
  background-color: #2d8cf0;
  color: white;
  padding: 12px 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

/* 主区域 */
.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 52px);
  overflow: hidden;
}

/* 聊天消息区域 */
.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.message {
  display: flex;
  margin-bottom: 24px;
  max-width: 92%;
}

.ai-message {
  margin-right: auto;
}

.user-message {
  flex-direction: row-reverse;
  margin-left: auto;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
}

.user-message .avatar {
  background-color: #2d8cf0;
  color: white;
}

.ai-message .avatar {
  background-color: #19be6b;
  color: white;
  margin-right: 12px;
}

.user-message .avatar {
  margin-left: 12px;
}

.message-content {
  background-color: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
}

.ai-message .message-content {
  border-top-left-radius: 2px;
}

.user-message .message-content {
  background-color: #ecf5ff;
  border-top-right-radius: 2px;
}

.message-content p {
  margin: 0;
  line-height: 1.5;
}

/* 附件样式 */
.attachment-card {
  margin-top: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
}

.attachment-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f7f7f7;
  cursor: pointer;
}

.attachment-icon {
  margin-right: 8px;
  font-style: normal;
}

.attachment-name {
  flex: 1;
  font-size: 14px;
}

.attachment-action {
  color: #2d8cf0;
  font-size: 13px;
}

.attachment-content {
  padding: 12px;
  background-color: #fff;
}

.report-content {
  font-size: 14px;
  line-height: 1.6;
}

.report-content h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 16px;
  color: #17233d;
}

.report-content p {
  margin-bottom: 8px;
}

.report-image {
  margin-top: 16px;
  text-align: center;
}

.report-image img {
  max-width: 100%;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

.image-caption {
  margin-top: 6px;
  font-size: 13px;
  color: #515a6e;
}

/* AI分析结果样式 */
.ai-analysis {
  font-size: 14px;
}

.ai-analysis h3 {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 16px;
  color: #17233d;
}

.diagnosis-section {
  margin-bottom: 16px;
}

.diagnosis-section h4 {
  margin: 0 0 8px 0;
  font-size: 15px;
  color: #2d8cf0;
}

.diagnosis-section p {
  margin: 4px 0;
}

.diagnosis-section ul {
  margin: 8px 0;
  padding-left: 20px;
}

.diagnosis-section li {
  margin-bottom: 6px;
}

/* 输入区域 */
.input-area {
  background-color: white;
  border-top: 1px solid #e8eaec;
  padding: 12px 20px 20px;
}

.attachment-bar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.selected-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-file {
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 13px;
}

.remove-btn {
  background: none;
  border: none;
  color: #999;
  margin-left: 6px;
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  display: flex;
  align-items: center;
}

.attach-button {
  background: none;
  border: none;
  color: #2d8cf0;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.attach-icon {
  margin-right: 4px;
  font-style: normal;
}

.message-input {
  display: flex;
  align-items: flex-end;
}

textarea {
  flex: 1;
  min-height: 80px;
  max-height: 150px;
  padding: 10px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.send-button {
  margin-left: 12px;
  background-color: #2d8cf0;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  height: 36px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.send-button:hover {
  background-color: #5cadff;
}

.send-button:disabled {
  background-color: #c5c8ce;
  cursor: not-allowed;
}
</style>